import React, { useEffect, useRef, useState } from "react";
import Roles from "./Roles";
import DeviceUserRole from "./DeviceUserRole";
import PlusIcon from "../../images/PlusIcon";
import { webNavigate } from "../../services/navigate.service";
import {
  extractValues,
  updateUrlParams,
} from "../../validation/ValidationInput";
import { useAccessContext } from "../../hook/useAccessContext";
import { RoleTab, resolveRoleTab, roleTabsFor } from "../../services/accessContext";
import { useResponsive } from "../../hook/useResponsive";
import { RoleStyles } from "./roleShared";

const TAB_LABELS: Record<RoleTab, string> = {
  system: "Web roles",
  device: "Device roles",
  support: "InnovX support",
};

const TAB_EXPLAINERS: Record<RoleTab, string> = {
  system: "What people can open in the back office. Web roles are shared by every company and managed by InnovX.",
  device: "What staff can do on the till. Device roles belong to this company.",
  support: "What InnovX staff can do when they open a customer company.",
};

const ADD_LABELS: Record<RoleTab, string> = {
  system: "Add web role",
  device: "Add device role",
  support: "Add support role",
};

const ListRole = () => {
  const access = useAccessContext();
  const { isTouchLayout } = useResponsive();
  const requestedTab = () => (extractValues(location.search.split("&")) as any).tab;
  // Read before the list mounts so the first render already shows the URL's tab.
  const [tab, setTab] = useState<RoleTab>(() => resolveRoleTab(requestedTab(), access));
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tr = (key: string) => (language && language[key] ? language[key] : key);
  const tabs = roleTabsFor(access);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  // The access context can arrive after the first render: honour or drop ?tab=support then.
  useEffect(() => {
    setTab(resolveRoleTab(requestedTab(), access));
  }, [access]);
  // Back / Forward on this page: re-read ?tab, which mounts the other list.
  useEffect(() => {
    const path = location.pathname;
    const handlePopState = () => {
      if (location.pathname !== path) return;
      setTab(resolveRoleTab(requestedTab(), access));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [access]);
  /** Switching tabs mounts the other list, which reads page 1 / no search from the URL. */
  const handleChangeTab = (value: RoleTab) => {
    if (value === tab) return;
    setTab(value);
    webNavigate(updateUrlParams({ tab: value, page: 1, limit: 50 }));
  };
  // Arrow keys, Home and End move between tabs and select them.
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = tabs.indexOf(tab);
    let next = -1;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    handleChangeTab(tabs[next]);
    tabRefs.current[tabs[next]]?.focus();
  };
  // Web and support roles are added by InnovX role editors only; device roles by the company.
  const canAdd = tab === "device" || access.canEditRoles;
  const handleAdd = () => {
    if (tab === "device") webNavigate("/" + preLanguage + "/deviceUserRole/new");
    else if (tab === "support") webNavigate("/" + preLanguage + "/roles/new?scope=support");
    else webNavigate("/" + preLanguage + "/roles/new");
  };
  return (
    <div
      className="h-full font-inter"
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
    >
      <RoleStyles />
      <div style={{ flex: "none" }}>
        <h1
          style={{ margin: 0, color: "#222525", fontSize: "20px", lineHeight: "130%", fontWeight: 500 }}
        >
          {tr("Roles")}
        </h1>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginTop: "12px",
          }}
        >
          <div role="tablist" aria-label={tr("Role types")} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
            {tabs.map((value) => {
              const active = tab === value;
              return (
                <button
                  key={value}
                  ref={(el) => {
                    tabRefs.current[value] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`role-tab-${value}`}
                  aria-selected={active}
                  aria-controls="role-tabpanel"
                  tabIndex={active ? 0 : -1}
                  className="role-tab"
                  onClick={() => handleChangeTab(value)}
                  onKeyDown={handleTabKeyDown}
                  style={{
                    color: active ? "#FFFFFF" : "#4A5353",
                    minHeight: isTouchLayout ? "44px" : "32px",
                    padding: "6px 14px",
                    borderRadius: "30px",
                    backgroundColor: active ? "#0D5759" : "transparent",
                    border: active ? "1px solid transparent" : "1px solid #C9D7D8",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    lineHeight: "130%",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {tr(TAB_LABELS[value])}
                </button>
              );
            })}
          </div>
          {canAdd && (
            <button
              type="button"
              className="role-primary"
              onClick={handleAdd}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "8px 24px 8px 16px",
                backgroundColor: "#0F7679",
                borderRadius: "30px",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span style={{ width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PlusIcon />
              </span>
              <span style={{ color: "#FFFFFF", fontSize: "14px", lineHeight: "130%", fontWeight: 500 }}>
                {tr(ADD_LABELS[tab])}
              </span>
            </button>
          )}
        </div>
        <p style={{ margin: "12px 0 0", fontSize: "14px", lineHeight: "20px", color: "#4A5353" }}>
          {tr(TAB_EXPLAINERS[tab])}
        </p>
      </div>
      <div
        id="role-tabpanel"
        role="tabpanel"
        aria-labelledby={`role-tab-${tab}`}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {tab === "device" ? (
          <DeviceUserRole tab={tab} />
        ) : (
          <Roles key={tab} tab={tab} scope={tab === "support" ? "support" : undefined} />
        )}
      </div>
    </div>
  );
};
export default ListRole;
